import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { StreamsService } from 'src/app/services/streams.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { StreamEngineService } from 'src/app/services/stream-engine.service';
import { simplifyStatus } from '../../service-status-icon/service-status-icon.component';
import { SocketService } from 'src/app/services/socket.service';
import { Worker } from 'src/app/services/predict-engine.service';
import { SelectionModel } from '@angular/cdk/collections';

@Component({
    selector: 'app-streams-widget',
    templateUrl: './streams-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './streams-widget.component.scss'],
    standalone: false
})
export class StreamsWidgetComponent extends BaseWidgetComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  @Output() onOpenDetail = new EventEmitter<{ parentId: string; serviceName: string }>();

  public displayedColumns: string[] = ['select', 'name', 'status', 'host', 'adapter', 'started', 'detail'];
  public isLoading = true;
  public dataSource = new MatTableDataSource<any>([]);
  public selection = new SelectionModel<any>(true, []);
  public serviceCounts = { active: 0, warn: 0, error: 0, inactive: 0, unknown: 0, all: 0, known: 0 };
  private lastClickedRow: any = null;

  public get allSelectedCanBeStarted() {
    return this.selection.selected.reduce((acc, s) => acc && s.rendering?.statusClass !== 'active', true);
  }

  public get allSelectedCanBeStopped() {
    return this.selection.selected.reduce((acc, s) => acc && s.rendering?.statusClass === 'active', true);
  }

  constructor(
    private _streamsService: StreamsService,
    private _streamEngineService: StreamEngineService,
    private _sockets: SocketService,
    private _changeDetectorRef: ChangeDetectorRef
  ) {
    super();
    if ((<any>window).streams) (<any>window).streams.push(this);
    else (<any>window).streams = [this];
  }

  async ngOnInit(): Promise<void> {
    await this.init();

    this._sockets.joinRoom('stream');
    this._subs.add = this._sockets.subscribeToRoomEvents('stream', (data: { service: Worker }) => {
      const { service } = data;
      const localName = service.name.replace(`lit-stream@${this.teamName}:`, '');
      const stream = this.dataSource.data.find((worker: Worker) => worker.name === localName);
      if (stream) {
        const oldStatusClass = stream.rendering?.statusClass;
        Object.assign(stream, { ...service, name: localName, status: simplifyStatus(service) });
        const newStatusClass = this.updateStatus(stream);
        if (oldStatusClass && oldStatusClass !== 'unknown') this.serviceCounts[oldStatusClass]--;
        if (newStatusClass !== 'unknown') this.serviceCounts[newStatusClass]++;
        this._changeDetectorRef.detectChanges();
      }
    });

    this._subs.add = this._streamEngineService.serviceClick$.subscribe((response) => {
      if (response.parentId !== this.id) return;
      this.onOpenDetail.emit(response);
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this._sockets.leaveRoom('stream');
    this._subs.dispose();
  }

  async init(reload: boolean = false) {
    this.loadData().then(() => (this.isLoading = false));
  }

  async loadData() {
    const [streams, workers] = await Promise.all([
      this._streamsService.loadAsync(this.teamName),
      this._streamsService.getWorkersAsync(this.teamName)
    ]);
    if (!streams) return;

    const workerMap = Object.fromEntries((workers || []).map((worker) => [worker.name, worker]));
    this.serviceCounts = { active: 0, warn: 0, error: 0, inactive: 0, unknown: 0, all: 0, known: 0 };
    this.dataSource.data = streams.map((stream) => {
      const worker = workerMap[stream.name];
      const row = worker
        ? { ...stream, status: worker.status, active: worker.active, started: worker.started }
        : { ...stream };
      this.processStats(row);
      return row;
    });
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  // Selection
  isAllSelected(): boolean {
    return this.selection.selected.length === this.dataSource.filteredData.length;
  }

  selectAll() {
    this.dataSource.filteredData.forEach((row) => this.selection.select(row));
  }

  onRowClick(row: any, event: MouseEvent) {
    if (event.shiftKey && this.lastClickedRow) {
      event.preventDefault();
      this.selectRange(this.lastClickedRow, row);
    } else {
      this.selection.toggle(row);
    }
    this.lastClickedRow = row;
    this._changeDetectorRef.detectChanges();
  }

  private selectRange(fromRow: any, toRow: any) {
    const data = this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort);
    const fromIndex = data.findIndex((r: any) => r.name === fromRow.name);
    const toIndex = data.findIndex((r: any) => r.name === toRow.name);

    if (fromIndex === -1 || toIndex === -1) return;

    const startIndex = Math.min(fromIndex, toIndex);
    const endIndex = Math.max(fromIndex, toIndex);

    for (let i = startIndex; i <= endIndex; i++) {
      if (!this.selection.isSelected(data[i])) {
        this.selection.select(data[i]);
      }
    }
  }

  toggleSelection(row: any) {
    this.selection.toggle(row);
  }

  // Service status
  updateStatus(row: any) {
    const statusClass = simplifyStatus({ active: row.active, status: row.status });
    row.rendering = { statusClass };
    return statusClass;
  }

  processStats(row: any) {
    const statusClass = this.updateStatus(row);
    this.serviceCounts[statusClass] += 1;
    this.serviceCounts.all += 1;
    if (statusClass !== 'unknown') this.serviceCounts.known += 1;
  }

  $updateStatus($event: Event, row: any, action: 'start' | 'stop' | 'restart') {
    $event.stopPropagation();
    row.status = 'updating';
    this._serviceAction(row.name, action);
  }

  // Bulk actions
  $clickStartSelected() {
    this._updateSelected();
    Promise.all(this.selection.selected.map((s) => this._serviceAction(s.name, 'start')));
  }

  $clickRestartSelected() {
    this._updateSelected();
    Promise.all(this.selection.selected.map((s) => this._serviceAction(s.name, 'restart')));
  }

  $clickStopSelected() {
    this._updateSelected();
    Promise.all(this.selection.selected.map((s) => this._serviceAction(s.name, 'stop')));
  }

  private _updateSelected() {
    for (let selected of this.selection.selected) {
      selected.status = 'updating';
      this.updateStreamRow(selected);
    }
  }

  private _serviceAction(name: string, action: 'start' | 'stop' | 'restart') {
    const fn = action === 'start' ? this._streamEngineService.start
      : action === 'stop' ? this._streamEngineService.stop
      : this._streamEngineService.restart;
    return new Promise<void>((resolve) => {
      const sub = fn.call(this._streamEngineService, this.teamName, name).subscribe((response) => {
        sub.unsubscribe();
        if (response) this.updateStreamRow(response);
        resolve();
      });
    });
  }

  updateStreamRow(updated: any) {
    let name = updated.name;
    if (name && name.includes(':')) {
      const lastIndex = name.lastIndexOf(':');
      name = name.substring(lastIndex + 1);
    }

    const row: any = this.dataSource.filteredData.find((x) => x.name === name);
    if (!row) return;

    const oldStatusClass = row.rendering?.statusClass;
    if (oldStatusClass && oldStatusClass !== 'unknown') this.serviceCounts[oldStatusClass]--;
    Object.assign(row, { status: updated.status, active: updated.active, started: updated.started });
    const newStatusClass = this.updateStatus(row);
    if (newStatusClass !== 'unknown') this.serviceCounts[newStatusClass]++;
    this._changeDetectorRef.detectChanges();
  }

  openDetail($event: Event, row: any) {
    $event.stopPropagation();
    this._streamEngineService.serviceClick$.next({ parentId: this.id, serviceName: row.name });
  }

  add() {
    this._streamEngineService.serviceClick$.next({ parentId: this.id, serviceName: 'add' });
  }
}
